import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import { employeeKeys } from '@/lib/query-keys';
import { employeeService } from '@/services/payroll.service';
import { useEmployeeFilterStore } from '@/stores/employeeFilterStore';
import type { StructureLineInput } from '@/types/payroll';

/** Surfaces the API's own message (e.g. "code already in use") rather than a generic one. */
function apiMessage(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  return message || fallback;
}

export function useEmployees() {
  const queryClient = useQueryClient();
  const filters = useEmployeeFilterStore();

  const employeesQuery = useQuery({
    queryKey: employeeKeys.list(filters.current),
    queryFn: () => employeeService.getAll(filters.current),
    placeholderData: keepPreviousData,
  });

  const createEmployee = useMutation({
    mutationFn: (data: Record<string, unknown>) => employeeService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.all });
      toast.success('Employee created');
    },
    onError: (e) => toast.error(apiMessage(e, 'Failed to create employee')),
  });

  const updateEmployee = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => employeeService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: employeeKeys.detail(id) });
      toast.success('Employee updated');
    },
    onError: (e) => toast.error(apiMessage(e, 'Failed to update employee')),
  });

  const deleteEmployee = useMutation({
    mutationFn: (id: string) => employeeService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
      toast.success('Employee deleted');
    },
    onError: (e) => toast.error(apiMessage(e, 'Failed to delete employee')),
  });

  return {
    employees: employeesQuery.data?.data ?? [],
    meta: employeesQuery.data?.meta,
    isLoading: employeesQuery.isLoading,
    filters,
    createEmployee,
    updateEmployee,
    deleteEmployee,
    exportCsv: () => employeeService.exportCsv(filters.current),
  };
}

export function useEmployee(id: string) {
  const query = useQuery({
    queryKey: employeeKeys.detail(id),
    queryFn: () => employeeService.getById(id),
    enabled: !!id,
  });
  return { employee: query.data?.data, isLoading: query.isLoading };
}

export function useNextEmployeeCode(enabled = true) {
  const query = useQuery({
    queryKey: employeeKeys.nextCode(),
    queryFn: () => employeeService.getNextCode(),
    enabled,
  });
  return { nextCode: query.data?.data?.nextCode };
}

export function useSalaryStructures(employeeId: string) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: employeeKeys.structures(employeeId),
    queryFn: () => employeeService.getSalaryStructures(employeeId),
    enabled: !!employeeId,
  });

  const createStructure = useMutation({
    mutationFn: (data: {
      effectiveFrom: string; ctcAnnual: number; currency?: string; notes?: string;
      revisionType?: string; reason?: string; lines: StructureLineInput[];
    }) => employeeService.createSalaryStructure(employeeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.structures(employeeId) });
      queryClient.invalidateQueries({ queryKey: employeeKeys.detail(employeeId) });
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
      toast.success('Salary revision saved');
    },
    onError: (e) => toast.error(apiMessage(e, 'Failed to save salary structure')),
  });

  return {
    structures: query.data?.data ?? [],
    isLoading: query.isLoading,
    createStructure,
  };
}

/**
 * Employees who can actually be paid — active, and with a salary structure on
 * record. Deliberately a standalone query rather than the shared employee
 * filter store: setting filters on that store would persist and silently
 * narrow the Employees list page the next time it was opened.
 */
export function usePayableEmployees() {
  const query = useQuery({
    queryKey: [...employeeKeys.lists(), { payable: true }],
    queryFn: () => employeeService.getAll({
      status: 'ACTIVE',
      hasSalaryStructure: 'true',
      limit: 100,
      sortBy: 'employeeCode',
      sortOrder: 'asc',
    }),
    staleTime: 30_000,
  });
  return { employees: query.data?.data ?? [], isLoading: query.isLoading };
}

/**
 * Warns which already-issued payslips a proposed effective date reaches back
 * over. Back-dating is permitted — issued payslips keep their frozen figures —
 * so this informs rather than blocks.
 */
export function useBackdateImpact(employeeId: string, effectiveFrom: string) {
  const query = useQuery({
    queryKey: [...employeeKeys.detail(employeeId), 'backdate-impact', effectiveFrom],
    queryFn: () => employeeService.backdateImpact(employeeId, effectiveFrom),
    enabled: !!employeeId && !!effectiveFrom,
    staleTime: 10_000,
  });
  return { impact: query.data?.data };
}
